import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, ListGroup, Alert, Form, Accordion, Spinner, Dropdown } from "react-bootstrap";
import styles from "./Inbox.module.css";
import { FaPen, FaEye, FaStar, FaRegCommentDots } from "react-icons/fa";

//ICONS
import { FiArrowUpRight, FiArrowDownRight, FiClock, FiSearch } from "react-icons/fi";

import ResourceViewerModal from "./modals/ResourceViewerModal";
import CommunicationFormModal from "./modals/CommunicationFormModal";
import QuickThanksModal from "./modals/QuickThanksModal";
import CommQueryBuilderModal from "./modals/CommQueryBuilderModal";
import ThreadedMessageViewer from "./components/ThreadedMessageViewer"; // Adjust path if needed
import InfoBar from "./components/Infobar";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { addMessage, setPageNumber, clearNewMessages, clearMessages } from "./slices/messageSlice"; // Adjust path if needed
import { promotePending } from "./store/thunks/promotePending"; // path may vary

//Services
import { fetchProviderByReference } from "./services/providerService";
import { fetchPatientByReference } from "./services/patientService";
import { fetchEncounterByReference } from "./services/encounterService";
import { useStore } from "react-redux";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import tooltipRegistry from "./tooltips/tooltipRegistry";
import { hasBeenReadByCurrentUser, isSelfMessage, validateCommunications, resolveName, filterLocalMessages, getStarredCount, getUrgentCount, groupByDateRange } from "./utils/messageUtils";
import { formatDate } from "./utils/dateUtils";
import { fetchNewMessages } from "./store/thunks/fetchNewMessages"; // adjust path if needed
import { renderCommunicationAttachments, renderPriorityBadge, getDirectionIcon } from "./utils/renderUtils";
import { clampPage } from "./utils/pagination";


function Inbox({ contextProvider, contextPatient, contextEncounter, error, fhirClient, communicationService }) {
    const dispatch = useDispatch();
    const store = useStore();
    const [selectedComm, setSelectedComm] = useState(null);
    const [fetchedPatient, setFetchedPatient] = useState(null);
    const [fetchedProvider, setFetchedProvider] = useState(null);
    const [fetchedEncounter, setFetchedEncounter] = useState(null);
    const [fetchError, setFetchError] = useState(null);

    //Pagination Control
    const currentPageUrl = useSelector(state => state.messages.currentPageUrl);
    const nextPageUrl = useSelector(state => state.messages.nextPageUrl);
    const prevPageUrl = useSelector(state => state.messages.prevPageUrl);
    const totalRecords = useSelector(state => state.messages.total);
    const currentPage = useSelector(state => state.messages.currentPageNumber);
    const hasNewMessage = useSelector(state => state.messages.hasNewMessage);

    const messagesToShow = useSelector(state => {
        const page = state.messages.pageMap[currentPageUrl];
        if (!currentPageUrl || !page || !page.ids) return [];
        //console.log("Page", page);
        //console.log("CURRENT PAGE URL:", currentPageUrl);
        // Memoized: reuses the same array if the ids and byId haven't changed
        return page.ids.map(id => state.messages.byId[id]);
    }, shallowEqual);

    const [viewResource, setViewResource] = useState(null);
    const [showViewer, setShowViewer] = useState(false);
    const [showThanksModal, setShowThanksModal] = useState(false);
    const [replyToCommId, setReplyToCommId] = useState(null);
    const [viewMode, setViewMode] = useState("received");
    const [collapsedThreads, setCollapsedThreads] = useState({});
    const [anchorTopics, setAnchorTopics] = useState({});
    const byId = useSelector((state) => state.messages.byId);
    const providers = useSelector(state => state.providers);
    //const patients = useSelector(state => state.patients);
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [submissionResult, setSubmissionResult] = useState("");
    const [defaultCollapseState, setDefaultCollapseState] = useState(false); // true = collapsed by default
    const [groupingMode, setGroupingMode] = useState(localStorage.getItem("inboxGroupingMode") || "date");
    const [loading, setLoading] = useState(true);
    const commArray = byId && typeof byId === "object" ? Object.values(byId) : [];
    const [collapseManuallyToggled, setCollapseManuallyToggled] = useState(false);
    const [modalCommDetails, setModalCommDetails] = useState(null);
    const [showCommModal, setShowCommModal] = useState(false);
    const [groupByAnchor, setGroupByAnchor] = useState(() => {
        const stored = localStorage.getItem("groupByAnchor");
        return stored === null ? true : stored === "true"; // Default to grouped
    });
    const [isReplyMode, setIsReplyMode] = useState(false);
    const [validatedMessages, setValidatedMessages] = useState([]);
    const validatedIdsRef = useRef(new Set()); // tracks all validated message IDs

    //CONFIG--------------------------------------------------------------------------
    const pageSize = (process.env.REACT_APP_INBOX_PAGE_SIZE);

    //-------------------------------------------------------------------------------

    // Always ensure at least 1 page
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

    //console.log("Total pages", totalPages);
    //console.log("DEBUG: totalRecords =", totalRecords);
    //console.log("DEBUG: pageSize =", pageSize);
    //console.log("DEBUG: nextPageUrl =", nextPageUrl);
    //console.log("DEBUG: currentPage =", currentPage);

    useEffect(() => {
        const fixed = clampPage(currentPage, totalPages);
        if (fixed !== currentPage) {
            dispatch(setPageNumber(fixed));
        }
    }, [currentPage, totalPages, dispatch]);

    const getDirectionLabel = (key) => {
        switch (key) {
            case "incoming": return "Inbox";
            case "outgoing": return "Sent";
            default: return "Inbox";
        }
    };

    const getFlagLabel = (key) => {
        switch (key) {
            case "all": return "All";
            case "unread": return "Unread";
            case "urgent": return "Urgent/STAT";
            case "starred": return "Starred";
            case "snoozed": return "Snoozed";
            default: return "All";
        }
    };


    // Get all unique practitioner IDs from the Communication's recipients and sender
    const allProviderIds = [
        ...(selectedComm?.recipient?.map(r => r.reference.split("/")[1]) || []),
        selectedComm?.sender?.reference?.split("/")[1]
    ].filter(Boolean);

    // Remove duplicates and the contextProvider
    const contextProviderId = contextProvider?.id; // <-- Add this line before using it
    const uniqueProviderIds = Array.from(new Set(allProviderIds)).filter(id => id !== contextProviderId);

    // Map to provider resources, fallback to minimal Practitioner if not found
    const resolvedRecipients = uniqueProviderIds.map(id =>
        store.getState().providers?.[id] || { resourceType: "Practitioner", id }
    );



    //Direction filters---------------------------------------------------    
    const [directionFilter, setDirectionFilter] = useState("incoming"); // 'incoming', 'outgoing', or 'both'

    //Local filters
    const [flag, setFlag] = useState("all"); // starred, snoozed, unread, urgent

    const [priorityFilter, setPriorityFilter] = useState({
        routine: true,
        urgent: true,
        stat: true,
        asap: true,
        undefined: true
    });
    const [payloadQuery, setPayloadQuery] = useState("");
    //----------------------------------------------------------------

    const [showBuilderModal, setShowBuilderModal] = useState(false);
    const handleCommModalClose = () => setShowCommModal(false);
    const handleCommModalOpen = async (comm) => {
        console.log("opening modall...............");
        try {
            setModalCommDetails(comm);
            setShowCommModal(true);
        } catch (err) {
            console.error("Failed to fetch patient details", err);
            setModalCommDetails(null);
            setShowCommModal(true);
        }
    };

    //We must accommodate for new messages arriving into the inbox and recalculate pagination.
    //const pageSize = 10;
    // virtual totals and pages should use localTotalRecords
    //const staticRecords = totalRecords; // from backend (now local)
    //let liveRecords = 100;
    //const virtualTotal = staticRecords + liveRecords;
    //const totalPages = Math.max(1, Math.ceil(virtualTotal / pageSize));
    //const adjustedCurrentPage = currentPage === 1 ? 1 : currentPage;

    /*
    
    <span className={styles.pageInfo}>
      &nbsp;&nbsp;{`Page ${adjustedCurrentPage} of ${totalPages}`}
    </span>
    
    */

    // When page changes — reset validation
    useEffect(() => {
        validatedIdsRef.current.clear();
        setValidatedMessages([]);
    }, [currentPageUrl]);

    useEffect(() => {
        //console.log("Validating messages useEffect...........");
        let isMounted = true; // prevent setting state if component unmounts
        async function validateAll() {
            if (!messagesToShow || messagesToShow.length === 0) {
                setValidatedMessages([]);
                return;
            }

            // Filter out messages that have already been validated
            const newMessages = messagesToShow.filter(msg => !validatedIdsRef.current.has(msg.id));
            //console.log("Validating new messages:", newMessages);   
            if (newMessages.length === 0) return; // nothing new to validate

            try {
                const validated = await validateCommunications(
                    newMessages,
                    fhirClient,
                    store
                );

                if (isMounted) {
                    setValidatedMessages(prev => {
                        // Merge old + new, deduplicate by ID
                        const combined = [...prev, ...validated];
                        const deduped = Array.from(
                            new Map(combined.map(msg => [msg.id, msg])).values()
                        );
                        return deduped;
                    });

                    // Record the IDs of newly validated messages
                    validated.forEach(msg => validatedIdsRef.current.add(msg.id));
                    //console.log(`✅ Validated ${validated.length}/${newMessages.length} new messages`);
                    console.log("VALIDATED", validated);
                }
            } catch (err) {
                console.error("Failed to validate communications:", err);
            }
        }

        validateAll();

        return () => {
            isMounted = false;
        };
    }, [messagesToShow]);



    useEffect(() => {
        if (contextPatient) {
            setFetchedPatient(contextPatient);
            console.log("✅ Synced fetchedPatient from contextPatient");
        }
    }, [contextPatient]);

    useEffect(() => {
        if (contextProvider) {
            setFetchedProvider(contextProvider);
            console.log("✅ Synced fetchedProvider from contextProvider");
        }
    }, [contextProvider]);

    // Optimistic local toggle for status flags (starred/snoozed) so UI updates immediately
    const STATUS_FLAG_URL = "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag";
    const toggleLocalFlag = (commId, flag, add) => {
        if (!commId) return;
        setValidatedMessages(prev => {
            return prev.map(m => {
                if (!m || m.id !== commId) return m;

                const exts = Array.isArray(m.extension) ? [...m.extension] : [];

                const matchesFlag = (ext) => {
                    if (!ext || ext.url !== STATUS_FLAG_URL) return false;
                    const userExt = ext.extension?.find(e => e.url === "user");
                    const flagExt = ext.extension?.find(e => e.url === "flag");
                    if (!userExt || !flagExt) return false;
                    const userRef = userExt.valueReference?.reference;
                    const flagCode = flagExt.valueCode;
                    return userRef === `Practitioner/${contextProvider?.id}` && flagCode === flag;
                };

                const has = exts.some(matchesFlag);

                if (add && !has) {
                    // append a new statusFlag extension for this user
                    const newExt = {
                        url: STATUS_FLAG_URL,
                        extension: [
                            { url: "user", valueReference: { reference: `Practitioner/${contextProvider?.id}` } },
                            { url: "flag", valueCode: flag }
                        ]
                    };
                    const updated = { ...m, extension: [...exts, newExt] };
                    // also update Redux authoritative copy optimistically
                    try {
                        const base = store.getState().messages.byId[commId] || m;
                        dispatch(addMessage({ ...base, ...updated }));
                    } catch (err) {
                        console.warn("Failed optimistic addMessage dispatch:", err);
                    }
                    return updated;
                }

                if (!add && has) {
                    const filtered = exts.filter(ext => !matchesFlag(ext));
                    const updated = { ...m, extension: filtered };
                    try {
                        const base = store.getState().messages.byId[commId] || m;
                        dispatch(addMessage({ ...base, ...updated }));
                    } catch (err) {
                        console.warn("Failed optimistic addMessage dispatch:", err);
                    }
                    return updated;
                }

                return m;
            });
        });
    };

    // Keep validatedMessages in sync with authoritative Redux messages (`byId`)
    // This ensures UI-derived counts (e.g., starred) update when message extensions change.
    useEffect(() => {
        if (!validatedMessages || validatedMessages.length === 0) return;
        let updated = false;
        const next = validatedMessages.map(vm => {
            const fresh = byId?.[vm.id];
            if (!fresh) return vm;

            // Quick compare: if extensions differ, replace the validated message with the fresh one
            const vmExt = vm?.extension ? JSON.stringify(vm.extension) : null;
            const freshExt = fresh?.extension ? JSON.stringify(fresh.extension) : null;
            if (vmExt !== freshExt) {
                updated = true;
                return { ...fresh };
            }

            // If other key fields changed (status, priority, payload), pick fresh as well
            if (vm.status !== fresh.status || vm.priority !== fresh.priority || JSON.stringify(vm.payload) !== JSON.stringify(fresh.payload)) {
                updated = true;
                return { ...fresh };
            }

            return vm;
        });

        if (updated) {
            setValidatedMessages(next);
        }
    }, [byId]);

    useEffect(() => {
        if (contextEncounter) {
            setFetchedEncounter(contextEncounter);
            console.log("✅ Synced fetchedEncounter from contextEncounter");
        }
    }, [contextEncounter]);

    // Polls for new messages. Dispatches them to the newMessages slice    
    const pollingStartedRef = useRef(false);

    useEffect(() => {
        if (pollingStartedRef.current) return;
        if (!contextProvider?.id || !fhirClient) return;

        console.log("✅ Starting 15s polling for fetchNewMessages");
        pollingStartedRef.current = true;

        const intervalId = setInterval(() => {
            console.log("⏳ Polling fetchNewMessages...");
            dispatch(fetchNewMessages(contextProvider.id, fhirClient, viewMode));
        }, 30000);

        return () => {
            console.log("🛑 Clearing polling interval");
            clearInterval(intervalId);
        };
    }, [contextProvider?.id, fhirClient]);


    //promotes optimistic messages from pending to confirmed
    useEffect(() => {
        if (!selectedComm?.id) return;
        const anchorRef = selectedComm.partOf?.[0]?.reference || `Communication/${selectedComm.id}`;
        const intervalId = setInterval(() => {
            dispatch(promotePending(anchorRef, fhirClient)); //promotes pending messages
        }, 30000); // 🔁 every 15 seconds

        return () => clearInterval(intervalId);
    }, [selectedComm?.id, dispatch]);


    useEffect(() => {
        if (!communicationService || !contextProvider?.id || !fhirClient) return;

        setLoading(true);

        const fetchData = async () => {
            try {
                await fetchAndGroup();
            } catch (err) {
                console.error("Error fetching messages:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [communicationService, contextProvider?.id, viewMode, fhirClient, groupByAnchor]);


    // 🔁 Keeps defaultCollapseState fresh in fetchAndGroup
    const collapseRef = useRef(defaultCollapseState);
    useEffect(() => {
        collapseRef.current = defaultCollapseState;
    }, [defaultCollapseState]);

    const groupCommunications = (comms) => {
        const threads = {};
        comms.forEach(comm => {
            const anchor = comm.partOf?.[0]?.reference || comm.id;
            if (!threads[anchor]) threads[anchor] = [];
            threads[anchor].push(comm);
        });
        return threads;
    };

    const loadNextPage = async () => {
        setLoading(true);
        setSelectedComm(null);
        setFetchedProvider(null);
        setFetchedPatient(null);
        setFetchedEncounter(null);
        if (!nextPageUrl || !communicationService) {
            setLoading(false);
            return;
        }
        await fetchAndGroup(nextPageUrl);
        setLoading(false);
    };

    const loadPrevPage = async () => {
        setLoading(true);
        setSelectedComm(null);
        setFetchedProvider(null);
        setFetchedPatient(null);
        setFetchedEncounter(null);
        if (!prevPageUrl || !communicationService) {
            setLoading(false);
            return;
        }
        await fetchAndGroup(prevPageUrl);
        setLoading(false);
    };

    async function handleRefresh() {
        console.log("Refreshing…");

        setLoading(true);

        // Reset local UI state
        setSelectedComm(null);
        setFetchedProvider(null);
        setFetchedPatient(null);
        setFetchedEncounter(null);

        // 👉 TRUE HARD REFRESH: wipe message cache before fetching
        dispatch(clearMessages());

        // Re-fetch from the server cleanly
        await fetchAndGroup();

        // Reset pagination
        dispatch(setPageNumber(1));

        // Reset “new messages” indicator
        dispatch(clearNewMessages());

        setLoading(false);
    }


    /** fetches, enriches, and then dispatches to redux.
     * Fetches a page of Communication resources and prepares state:
     * - Groups messages based on `groupByAnchor`
     * - Expands/collapses each thread based on `collapseRef.current`
     * - Resolves anchor topics for labeling grouped threads
     * - Updates pagination and communication state
     */
    const fetchAndGroup = async (pageUrl = null, viewModeOverride = null) => {
        const effectiveViewMode = viewModeOverride || viewMode;
        setViewMode(effectiveViewMode);
        console.log("fetching and resolving pageUrl", pageUrl, "viewMode:", effectiveViewMode);
        try {
            const fetchPage = await communicationService.getCommunicationsPage(
                contextProvider.id,
                pageSize,
                pageUrl,
                effectiveViewMode,
                store,
                "",
                false  //force refresh
            );

            //const { data, nextPageUrl, prevPageUrl, total } = fetchPage;
            const { data } = fetchPage;
            console.log(fetchPage);

            if (!data || data.length === 0) {
                setCollapsedThreads({});
                setAnchorTopics({});
                return;
            }

            const groupByIndividualMessage = (comms) => {
                const threads = {};
                comms.forEach((comm) => {
                    threads[comm.id] = [comm];
                });
                return threads;
            };

            const grouped = groupByAnchor ? groupCommunications(data) : groupByIndividualMessage(data);

            setCollapsedThreads(prev => {
                const updated = { ...prev };
                for (const anchor of Object.keys(grouped)) {
                    if (!(anchor in updated)) {
                        updated[anchor] = collapseRef.current;
                    }
                }
                return updated;
            });

            //console.log(data);
            data.forEach(msg => {
                dispatch(addMessage({
                    ...msg,
                    meta: { isPending: false }
                }));
            });

            const anchorIds = new Set();
            data.forEach((comm) => {
                const ref = comm.partOf?.[0]?.reference;
                const id = ref?.split?.("/")[1];
                anchorIds.add(id || comm.id);
            });

            const fetchedAnchors = await Promise.all(
                Array.from(anchorIds).map(async (id) => {
                    try {
                        const isAnchor = true;
                        return await communicationService.fetchCommunication(id, store, { forceFetch: true }, isAnchor);
                    } catch (err) {
                        console.warn(`❌ Failed to fetch anchor ${id}`, err);
                        return null;
                    }
                })
            );

            //console.log("Fetched anchors.............................", fetchedAnchors);
            const anchorTopicMap = {};
            fetchedAnchors.forEach((anchor) => {
                if (anchor) {
                    const topic =
                        anchor.topic?.coding?.find((c) => c.display)?.display ||
                        anchor.topic?.text ||
                        "Conversation Thread";
                    anchorTopicMap[anchor.id] = topic;
                }

            });
            setAnchorTopics(anchorTopicMap);
            setValidatedMessages(data);

            await Promise.all(
                Array.from(anchorIds).map(async (anchorId) => {
                    const anchorRef = `Communication/${anchorId}`;
                    try {
                        //console.log("Writing conversation into redux ", anchorRef);
                        await communicationService.writeConversationIntoRedux(anchorRef, store);
                    } catch (err) {
                        console.warn(`❌ Failed to write conversation into redux for  anchor ${anchorId}`, err);
                    }
                })
            );


        } catch (error) {
            alert(error);
            console.error("💥 Error fetching communications:", error);
            setCollapsedThreads({});
            setAnchorTopics({});
        }
    };

    async function handleSelectComm(comm) {
        console.log(comm);
        setFetchError(null);

        try {
            const commId = comm.id;
            setReplyToCommId(commId);
            const anchorRef = comm.partOf?.[0]?.reference || "unknown";

            //console.log(`[Inbox] Handling Communication ${commId}`, comm);

            // ✅ Fetch Patient
            const fetchedPatientData = await fetchPatientByReference(
                comm.subject?.reference,
                fhirClient,
                store
            );
            setFetchedPatient(fetchedPatientData);

            // ✅ Fetch Provider
            const fetchedProviderData = await fetchProviderByReference(
                comm.sender?.reference,
                fhirClient,
                store
            );
            setFetchedProvider(fetchedProviderData);

            // ✅ Fetch Encounter
            const fetchedEncounterData = await fetchEncounterByReference(
                comm.encounter?.reference,
                fhirClient,
                store
            );
            setFetchedEncounter(fetchedEncounterData);

            // 🔄 Always fetch the latest Communication to check read receipts  
            //const freshComm = await fhirClient.request(`Communication/${commId}`);

            let freshComm;
            try {
                //console.log("fetching anchor");
                const isAnchor = false;
                freshComm = await communicationService.fetchCommunication(commId, store, { forceFetch: true }, isAnchor);
            } catch (err) {
                console.warn(`❌ Failed to fetch anchor ${commId}`, err);
            }

            //console.log(`[Inbox] Fetched fresh Communication`, freshComm);

            const existingReceipts =
                freshComm.extension?.filter(
                    ext => ext.url === "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt"
                ) || [];
            //console.log(`[Inbox] Existing read receipts:`, existingReceipts);

            // Check if this user already read it
            const alreadyRead = existingReceipts.some(ext =>
                ext.extension?.some(
                    e =>
                        e.url === "user" &&
                        e.valueReference?.reference === `Practitioner/${contextProvider.id}`
                )
            );
            //console.log(`[Inbox] Already read?`, alreadyRead);

            // Check if the message is self-sent
            const selfMessage = isSelfMessage(freshComm, contextProvider.id);
            //console.log(`[Inbox] Self message?`, selfMessage);

            // Add read receipt if needed
            if (!alreadyRead && !selfMessage) {
                //console.log(`[ReadReceipt] Adding read receipt for Communication ${commId}`);
                try {
                    await communicationService.addReadReceipt(commId, contextProvider.id, store);
                    console.log(`[ReadReceipt] Successfully added read receipt for ${commId}`);

                    //const refreshedComm = await fhirClient.request(`Communication/${commId}`);
                    let refreshedComm;
                    try {
                        const isAnchor = false;
                        refreshedComm = await communicationService.fetchCommunication(commId, store, { forceFetch: true }, isAnchor);
                    } catch (err) {
                        console.warn(`❌ Failed to fetch anchor ${commId}`, err);
                    }
 
                    // update selection and redux store so list reflects read state immediately
                    setSelectedComm(refreshedComm);
                    dispatch(addMessage({ ...refreshedComm, meta: { isPending: false } }));
                } catch (err) {
                    console.error(`[ReadReceipt] Error adding read receipt for ${commId}:`, err);
                }
            } else {
                console.log(
                    `[ReadReceipt] Skipped: already read or self-sent. commId=${commId}, userId=${contextProvider.id}`
                );
                // Use freshComm even if skipping to ensure selectedComm is up-to-date
                setSelectedComm(freshComm);
                dispatch(addMessage({ ...freshComm, meta: { isPending: false } }));
            }

            // Write conversation into Redux if anchorRef is valid
            if (anchorRef !== "unknown") {
                await communicationService.writeConversationIntoRedux(anchorRef, dispatch);
            } else {
                console.info(`[Inbox] Skipping writeConversationIntoRedux: invalid anchorRef`, anchorRef);
            }
        } catch (error) {
            console.error(`[Inbox] Error handling Communication ${comm.id}:`, error);
            setFetchError("Failed to load related patient/provider/encounter data.");
        }
    }


    const selectMessageById = async (targetId) => {
        setDirectionFilter("outgoing");
        const result = await communicationService.getCommunicationsPage(
            contextProvider.id,
            pageSize,
            null,
            "sent",   //sent (system=incoming) or received (system = outgoing)   
            dispatch,
            store,
            "",
            true
        );

        const fetchedMatch = result.data?.find(msg => msg.id === targetId);
        if (fetchedMatch) {
            setSelectedComm(fetchedMatch);
        } else {
            console.info("❌ Message not found after loading current view direction:", targetId);
        }
    };

    // Add this state to control which accordion items are open
    const [openAccordionKeys, setOpenAccordionKeys] = useState([]);

    // Helper to get all group keys for the current grouping mode
    const getAllGroupKeys = () => {
        return groupingMode !== "date"
            ? Object.entries(
                Object.values(byId).reduce((acc, comm) => {
                    let key = "Ungrouped";
                    if (groupingMode === "topic") {
                        key = comm.partOf?.[0]?.reference || `Communication/${comm.id}`;
                    } else if (groupingMode === "patient") {
                        key = comm.subject?.reference || "Unknown Patient";
                    } else if (groupingMode === "sender") {
                        key = comm.sender?.reference || "Unknown Sender";
                    }
                    acc[key] = acc[key] || [];
                    acc[key].push(comm);
                    return acc;
                }, {})
            ).map(([groupKey]) => groupKey)
            : [];
    };

    useEffect(() => {
        if (groupingMode === "date") return;

        const allKeys = getAllGroupKeys();

        setOpenAccordionKeys((prevKeys) => {
            if (!collapseManuallyToggled) {
                // Safe to apply defaultCollapseState on first render
                return defaultCollapseState ? [] : allKeys;
            }

            // ⚠️ Respect user override
            return prevKeys.filter((key) => allKeys.includes(key));
        });
    }, [groupingMode, byId]);


    const toolbar = (
        <div
            className="d-flex align-items-center justify-content-start gap-3"
            style={{ marginTop: "0.3rem" }}
        >

            {/* Grouping Select */}
            <div className="d-flex align-items-center">
                <Form.Label htmlFor="grouping-mode" className="mb-0 me-2 text-dark"></Form.Label>
                <Form.Select
                    id="grouping-mode"
                    value={groupingMode}
                    onChange={(e) => {
                        const mode = e.target.value;
                        setGroupingMode(mode);
                        localStorage.setItem("inboxGroupingMode", mode);
                    }}
                    className="mb-1"
                    style={{ width: "120px" }}
                >
                    <option value="date">By Date</option>
                    <option value="topic">By Topic</option>
                    <option value="patient">By Patient</option>
                    <option value="sender">By Sender</option>
                </Form.Select>
            </div>

            {/* Direction Filter Select */}
            <div className="d-flex align-items-center">
                <Dropdown
                    onSelect={async (key) => {
                        setLoading(true);
                        const directionViewMode = key === "incoming" ? "received" : "sent";
                        setDirectionFilter(key);
                        setViewMode(directionViewMode);
                        setSelectedComm(null);
                        setValidatedMessages([]);
                        dispatch(setPageNumber(1));

                        try {
                            await fetchAndGroup(null, directionViewMode);
                        } catch (err) {
                            console.error("Failed to fetch communications:", err);
                        } finally {
                            setLoading(false);
                        }
                    }}
                >
                    <Dropdown.Toggle
                        variant="outline-secondary"
                        id="direction-filter"
                        className={styles.dropdownToggle}
                    >
                        {getDirectionIcon(directionFilter)}
                        {getDirectionLabel(directionFilter)}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className={styles.dropdownMenu}>
                        <Dropdown.Item eventKey="incoming">
                            <FiArrowDownRight
                                style={{ color: "#dc3545", marginRight: "0.5rem" }}
                            />
                            Inbox
                        </Dropdown.Item>
                        <Dropdown.Item eventKey="outgoing">
                            <FiArrowUpRight
                                style={{ color: "#0d6efd", marginRight: "0.5rem" }}
                            />
                            Sent
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {/* Local Message filtering */}
            <div className="d-flex align-items-center">
                <Dropdown
                    onSelect={(key) => {
                        getFlagLabel(key);
                        setFlag(key);
                    }}
                >
                    <Dropdown.Toggle
                        variant="outline-secondary"
                        id="flag-filter"
                        className={styles.dropdownToggle}
                    >
                        {getDirectionIcon(directionFilter)}
                        {getFlagLabel(flag)}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className={styles.dropdownMenu}>
                        <Dropdown.Item eventKey="all">
                            <FaRegCommentDots style={{ marginRight: "0.5rem" }} />
                            All
                        </Dropdown.Item>

                        <Dropdown.Item eventKey="starred">
                            <FaStar style={{ color: "#ffc107", marginRight: "0.5rem" }} />
                            Starred
                        </Dropdown.Item>

                        <Dropdown.Item eventKey="snoozed">
                            <FiClock style={{ marginRight: "0.5rem" }} />
                            Snoozed
                        </Dropdown.Item>

                        <Dropdown.Item eventKey="unread">
                            <FaEye style={{ marginRight: "0.5rem" }} />
                            Unread
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {groupingMode !== "date" && (
                <Form.Check
                    type="switch"
                    id="expand-collapse-all"
                    label={
                        <span style={{ color: "black" }}>
                            {defaultCollapseState ? "Expand All" : "Collapse All"}
                        </span>
                    }
                    checked={!defaultCollapseState}
                    onChange={() => {
                        const newState = !defaultCollapseState;
                        const allKeys = getAllGroupKeys();
                        setDefaultCollapseState(newState);
                        setCollapseManuallyToggled(true);
                        setOpenAccordionKeys(newState ? [] : allKeys);
                        setCollapsedThreads((old) => {
                            const updated = { ...old };
                            allKeys.forEach((k) => (updated[k] = newState));
                            return updated;
                        });
                    }}
                    style={{ marginLeft: "1rem", marginTop: "0.4rem" }}
                />
            )}
        </div>
    );


    return (
        <Container fluid className="mt-1">
            <Row>
                {/* East Section (2/3 width) */}
                <Col md={5} className={styles.eastSection}>
                    <div>
                        {/* Pagination Controls */}
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            {/* Left Side: NEW Button */}
                            <div>
                                <OverlayTrigger placement="bottom" overlay={tooltipRegistry.composeButton} animation={false} >
                                    <Button className="mx-0"
                                        variant="outline-primary"
                                        onClick={() => {
                                            setReplyToCommId(null); // ✅ ensure compose flow uses contextPatient
                                            setSelectedComm(null); // 🔥 Clear selected thread
                                            setIsReplyMode(false); // 🔹 Explicitly mark as reply
                                            setShowReplyModal(true);

                                        }}
                                    >
                                        <FaPen className="mx-0" /> New
                                    </Button>
                                </OverlayTrigger>
                                <OverlayTrigger placement="bottom" overlay={tooltipRegistry.replyButton} animation={false} >
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => {
                                            setIsReplyMode(true); // 🔹 Explicitly mark as reply
                                            setShowReplyModal(true);
                                        }}
                                        className="mx-0"
                                        disabled={
                                            !fetchedProvider ||
                                            !selectedComm ||
                                            (fetchedEncounter &&
                                                typeof fetchedEncounter === "object" &&
                                                "error" in fetchedEncounter &&
                                                typeof fetchedEncounter.error === "number")
                                        }
                                    >
                                        Reply
                                    </Button>
                                </OverlayTrigger>

                                <Button variant="outline-primary" onClick={() => setShowBuilderModal(true)}>
                                    <FiSearch />
                                    Search
                                </Button>

                                <CommQueryBuilderModal
                                    show={showBuilderModal}
                                    onClose={() => setShowBuilderModal(false)}
                                    fhirClient={fhirClient}
                                    contextProvider={contextProvider}
                                    setSelectedComm={setSelectedComm}

                                />
                                <Button
                                    variant="outline-primary"
                                    onClick={handleRefresh}
                                    className={`mx-0 position-relative ${hasNewMessage ? styles.newMessageHighlight : ""}`}
                                >
                                    Refresh
                                    {hasNewMessage && (
                                        <span
                                            style={{
                                                position: "absolute",
                                                top: "-6px",
                                                right: "-6px",
                                                width: "18px",
                                                height: "18px",
                                                background: "red",
                                                borderRadius: "50%",
                                                border: "2px solid white",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "white",
                                                fontWeight: "bold",
                                                fontSize: "1rem",
                                                zIndex: 10,
                                                boxShadow: "0 0 6px 2px #ff000080"
                                            }}
                                            title="New message(s) have arrived!"
                                        >
                                            !
                                        </span>
                                    )}
                                </Button>
                            </div>

                            {/* Right Side: Pagination + Refresh */}
                            <div className="d-flex justify-content-between align-items-center text-end">
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={loadPrevPage}
                                    disabled={!prevPageUrl}
                                    className="mx-1 d-flex align-items-center"
                                    title="Previous Page"
                                >
                                    <span style={{ fontSize: "1.5rem", lineHeight: "1" }}>⏪</span>
                                </Button>

                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={loadNextPage}
                                    disabled={!nextPageUrl}
                                    className="mx-1 d-flex align-items-center"
                                    title="Next Page"
                                >
                                    <span style={{ fontSize: "1.5rem", lineHeight: "1" }}>⏩</span>
                                </Button>


                                <div className="d-flex align-items-center ms-3">
                                    <span className="ms-0 text-dark">Page&nbsp;</span>

                                    <input
                                        type="number"
                                        min={1}
                                        max={totalPages}
                                        value={currentPage}
                                        disabled
                                        onChange={(e) => {
                                            const input = Number(e.target.value);
                                            const page = clampPage(input, totalPages);
                                            dispatch(setPageNumber(page));
                                        }}
                                        className="form-control form-control-sm text-center"
                                        style={{ width: "50px" }}
                                    />

                                    {/* If you want the displayed total to match computeTotalPages, use totalPages here */}
                                    <span className="ms-1 text-dark">of {totalPages}</span>
                                </div>

                            </div>
                        </div>

                        {toolbar}


                        {error ? (
                            <Alert variant="danger">{error}</Alert>
                        ) : loading ? (
                            <div className={styles.spinnerContainer}>
                                <Spinner
                                    animation="border"
                                    role="status"
                                    className={styles.spinner}
                                />
                                <span className={styles.loadingText}>Loading Communications...</span>
                            </div>
                        ) : validatedMessages.length > 0 ? (
                            <>
                                {/* Bootstrap Accordion for groupings */}
                                {groupingMode !== "date" ? (
                                    <Accordion
                                        activeKey={openAccordionKeys}
                                        onSelect={(eventKey) => {
                                            setOpenAccordionKeys((prev) =>
                                                prev.includes(eventKey)
                                                    ? prev.filter((k) => k !== eventKey)
                                                    : [...prev, eventKey]
                                            );
                                        }}
                                    >
                                        {Object.entries(
                                            Object.values(validatedMessages).reduce((acc, comm) => {
                                                //console.log(messagesToShow);
                                                let key = "Ungrouped";
                                                if (groupingMode === "topic") {
                                                    key = comm.partOf?.[0]?.reference || `Communication/${comm.id}`;
                                                } else if (groupingMode === "patient") {
                                                    key = comm.subject?.reference || "Unknown Patient";
                                                } else if (groupingMode === "sender") {
                                                    key = comm.sender?.reference || "Unknown Sender";
                                                }
                                                acc[key] = acc[key] || [];
                                                acc[key].push(comm);
                                                return acc;
                                            }, {})
                                        )
                                            .sort(([, msgsA], [, msgsB]) => {
                                                const firstSentA = new Date(msgsA[0]?.sent || 0);
                                                const firstSentB = new Date(msgsB[0]?.sent || 0);
                                                return firstSentB - firstSentA;
                                            })
                                            .map(([groupKey, messages], idx) => {
                                                let label = "Conversations";
                                                if (groupingMode === "topic") {
                                                    const anchorId = groupKey?.split("/")?.[1];
                                                    label = anchorTopics[anchorId] || "Conversation Thread";
                                                } else if (groupingMode === "patient") {
                                                    const subjectRef = messages[0]?.subject?.reference;
                                                    label = resolveName(subjectRef, store, fhirClient) || "Unknown Patient";
                                                } else if (groupingMode === "sender") {
                                                    const senderRef = messages[0]?.sender?.reference;
                                                    label = resolveName(senderRef, store, fhirClient) || "Unknown Sender";
                                                }

                                                return (
                                                    <Accordion.Item eventKey={groupKey} key={groupKey}>
                                                        <Accordion.Header>
                                                            {label}
                                                            <span className="ms-2 text-muted">
                                                                ({messages.length} total • {
                                                                    messages.filter(comm => !hasBeenReadByCurrentUser(byId?.[comm.id] || comm, contextProvider?.id)).length
                                                                } unread
                                                                {getUrgentCount(messages) > 0 && (
                                                                    <> • <span className="text-danger fw-bold">🔥 {getUrgentCount(messages)} urgent</span></>
                                                                )}
                                                                {getStarredCount(messages, contextProvider?.id) > 0 && (
                                                                    <> • <span className="text-warning fw-bold"><FaStar style={{ marginRight: "0.3rem" }} />{getStarredCount(messages, contextProvider?.id, byId)} starred</span></>
                                                                )})
                                                            </span>
                                                        </Accordion.Header>
                                                        <Accordion.Body>
                                                            <ListGroup>
                                                                {filterLocalMessages(messages, {
                                                                    flag: flag,
                                                                    priorityFilter,
                                                                    practitionerId: contextProvider?.id
                                                                }, byId)
                                                                    .sort((a, b) => new Date(b.sent) - new Date(a.sent))
                                                                    .map((comm, index) => {
                                                                        //console.log(comm);
                                                                        const senderRef = comm.sender?.reference;
                                                                        const senderId = senderRef?.split("/")?.[1];
                                                                        const sender = senderId ? providers[senderId] : null;
                                                                        // Practitioner-aware status check
                                                                        const hasStatusFlagFor = (msg, type, practitionerId) => {
                                                                            const m = msg?.id ? (byId?.[msg.id] || msg) : msg;
                                                                            if (!Array.isArray(m?.extension)) return false;
                                                                            return m.extension.some(ext => {
                                                                                if (!ext || ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;
                                                                                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                                                                                const flagCode = ext.extension?.find(e => e.url === "flag")?.valueCode;
                                                                                const normalizedRef = userRef?.split?.("/")?.pop?.();
                                                                                return normalizedRef === practitionerId && flagCode === type;
                                                                            });
                                                                        };

                                                                        const isStarred = hasStatusFlagFor(comm, "starred", contextProvider?.id);
                                                                        const isSnoozed = hasStatusFlagFor(comm, "snoozed", contextProvider?.id);
                                                                        const senderName = resolveName(comm.sender?.reference, store, fhirClient) || "Unknown Sender";
                                                                        // read authoritative state from Redux if available
                                                                        const renderedMsg = comm?.id ? (byId?.[comm.id] || comm) : comm;
                                                                        const isUnread = !hasBeenReadByCurrentUser(renderedMsg, contextProvider?.id);
                                                                        const isOutgoing = senderRef === `Practitioner/${contextProvider?.id}`;
                                                                        const directionIcon = isOutgoing ? (
                                                                            <FiArrowUpRight
                                                                                style={{ color: "#0d6efd", marginRight: "0.0rem" }}
                                                                                title="Outgoing"
                                                                            />
                                                                        ) : (
                                                                            <FiArrowDownRight
                                                                                style={{ color: "#dc3545", marginRight: "0.0rem" }} // 🔴 red for incoming
                                                                                title="Incoming"
                                                                            />
                                                                        );
                                                                        return (
                                                                            <ListGroup.Item
                                                                                key={comm.id || index}
                                                                                action
                                                                                active={selectedComm?.id === comm.id}
                                                                                onClick={() => {
                                                                                    setSelectedComm(comm); // updates selection immediately
                                                                                    setTimeout(() => { handleSelectComm(comm); }, 0);
                                                                                }}

                                                                                onDoubleClick={() => handleCommModalOpen(comm)}
                                                                                className={
                                                                                    selectedComm?.id === comm.id
                                                                                        ? styles.selectedMessageItem
                                                                                        : styles.messageItem
                                                                                }
                                                                                style={{ marginLeft: "0.0rem" }}
                                                                            >
                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                    <div>
                                                                                        <div className="d-flex align-items-center">
                                                                                            {renderPriorityBadge(comm.priority)}
                                                                                            <strong
                                                                                                style={{
                                                                                                    marginLeft: "0.5rem",
                                                                                                    color: isUnread ? "#0d6efd" : "#444",
                                                                                                    fontWeight: isUnread ? "bold" : "normal"
                                                                                                }}
                                                                                            >
                                                                                                {comm.topic?.coding?.[0]?.display || "(No topic)"}
                                                                                            </strong>
                                                                                            <span style={{ marginLeft: "0.3rem" }}>{directionIcon}</span>
                                                                                            <span style={{ marginLeft: "0.5rem" }}>
                                                                                                {renderCommunicationAttachments(comm)}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div style={{ fontSize: "0.8rem", color: "#444" }}>
                                                                                            From: {senderName} —{" "}
                                                                                            {comm.payload?.[0]?.contentString?.slice(0, 100) ||
                                                                                                "(No content)"}
                                                                                        </div>
                                                                                        <small>Sent: {formatDate(comm.sent)}</small>
                                                                                    </div>

                                                                                    {/* ⭐ ⏰ Status controls */}
                                                                                    <div
                                                                                        className="d-flex align-items-center"
                                                                                        style={{ minWidth: "80px", justifyContent: "flex-end", gap: "0.75rem" }}
                                                                                    >
                                                                                        <FaStar
                                                                                            title={isStarred ? "Unstar this message" : "Star this message"}
                                                                                            className={`${styles.starIcon} ${isStarred ? styles.starIconActive : ""}`}
                                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                if (isStarred) {
                                                                                                    communicationService.removeStatusFlag(comm.id, "starred", contextProvider.id, dispatch);
                                                                                                    toggleLocalFlag(comm.id, "starred", false);
                                                                                                } else {
                                                                                                    communicationService.addStatusFlag(comm.id, "starred", contextProvider.id, dispatch);
                                                                                                    toggleLocalFlag(comm.id, "starred", true);
                                                                                                }
                                                                                            }}
                                                                                        />

                                                                                        <FiClock
                                                                                            title={isSnoozed ? "Unsnooze this message" : "Snooze this message"}
                                                                                            className={`${styles.snoozeIcon} ${isSnoozed ? styles.snoozeIconActive : ""}`}
                                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                if (isSnoozed) {
                                                                                                    communicationService.removeStatusFlag(comm.id, "snoozed", contextProvider.id, dispatch);
                                                                                                    toggleLocalFlag(comm.id, "snoozed", false);
                                                                                                } else {
                                                                                                    communicationService.addStatusFlag(comm.id, "snoozed", contextProvider.id, dispatch);
                                                                                                    toggleLocalFlag(comm.id, "snoozed", true);
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </ListGroup.Item>
                                                                        );
                                                                    })}
                                                            </ListGroup>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                );
                                            })}
                                    </Accordion>
                                ) : (
                                    <Accordion alwaysOpen defaultActiveKey={["Today", "Yesterday", "Last Week", "Older"]}>
                                        {Object.entries(groupByDateRange(validatedMessages)).map(([sectionLabel, messages]) => messages.length > 0 ? (
                                            <Accordion.Item eventKey={sectionLabel} key={sectionLabel}>
                                                <Accordion.Header>
                                                    {sectionLabel}
                                                    <span className="ms-2 text-muted">
                                                        ({messages.length} total • {
                                                            messages.filter(comm => !hasBeenReadByCurrentUser(byId?.[comm.id] || comm, contextProvider?.id)).length
                                                        } unread
                                                        {getUrgentCount(messages) > 0 && (
                                                            <> • <span className="text-danger fw-bold">🔥 {getUrgentCount(messages)} urgent</span></>
                                                        )}
                                                        {getStarredCount(messages, contextProvider?.id) > 0 && (
                                                            <> • <span className="text-warning fw-bold"><FaStar style={{ marginRight: "0.3rem" }} />{getStarredCount(messages, contextProvider?.id, byId)} starred</span></>
                                                        )})
                                                    </span>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <ListGroup>
                                                        {filterLocalMessages(messages, {
                                                            priorityFilter,
                                                            practitionerId: contextProvider?.id,
                                                            flag: flag, // or "snoozed", etc. depending on your current view
                                                            payloadQuery: payloadQuery
                                                        }, byId).map((comm, index) => {
                                                            const senderRef = comm.sender?.reference;
                                                            const senderName = resolveName(senderRef, store, fhirClient) || "Unknown Sender";
                                                            // prefer Redux-backed message to reflect read receipts
                                                            const renderedMsg = comm?.id ? (byId?.[comm.id] || comm) : comm;
                                                            const isUnread = !hasBeenReadByCurrentUser(renderedMsg, contextProvider?.id);
                                                            const isOutgoing = senderRef === `Practitioner/${contextProvider?.id}`;
                                                            const directionIcon = isOutgoing ? (
                                                                <FiArrowUpRight style={{ color: "#0d6efd" }} title="Outgoing" />
                                                            ) : (
                                                                <FiArrowDownRight style={{ color: "#dc3545" }} title="Incoming" />
                                                            );

                                                            //const hasAttachment = comm.payload?.some(p => p.contentAttachment);

                                                            // Practitioner-aware status check
                                                            const hasStatusFlagFor = (msg, type, practitionerId) => {
                                                                const m = msg?.id ? (byId?.[msg.id] || msg) : msg;
                                                                if (!Array.isArray(m?.extension)) return false;
                                                                return m.extension.some(ext => {
                                                                    if (!ext || ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;
                                                                    const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                                                                    const flagCode = ext.extension?.find(e => e.url === "flag")?.valueCode;
                                                                    const normalizedRef = userRef?.split?.("/")?.pop?.();
                                                                    return normalizedRef === practitionerId && flagCode === type;
                                                                });
                                                            };

                                                            const isStarred = hasStatusFlagFor(comm, "starred", contextProvider?.id);
                                                            const isSnoozed = hasStatusFlagFor(comm, "snoozed", contextProvider?.id);

                                                            return (
                                                                <ListGroup.Item
                                                                    key={comm.id || index}
                                                                    action
                                                                    active={selectedComm?.id === comm.id}
                                                                    onClick={() => {
                                                                        setSelectedComm(comm); // updates selection immediately
                                                                        setTimeout(() => {
                                                                            handleSelectComm(comm);
                                                                        }, 0);
                                                                    }}
                                                                    onDoubleClick={() => handleCommModalOpen(comm)}
                                                                    className={selectedComm?.id === comm.id ? styles.selectedMessageItem : styles.messageItem}
                                                                    style={{ marginLeft: "0.0rem" }}
                                                                >
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            <div className="d-flex align-items-center">
                                                                                {renderPriorityBadge(comm.priority)}
                                                                                <strong style={{
                                                                                    marginLeft: "0.5rem",
                                                                                    color: isUnread ? "#0d6efd" : "#444",
                                                                                    fontWeight: isUnread ? "bold" : "normal"
                                                                                }}>
                                                                                    {comm.topic?.coding?.[0]?.display || "(No topic)"}
                                                                                </strong>
                                                                                <span style={{ marginLeft: "0.3rem" }}>{directionIcon}</span>
                                                                                <span style={{ marginLeft: "0.5rem" }}>
                                                                                    {renderCommunicationAttachments(comm)}
                                                                                </span>
                                                                            </div>
                                                                            <div style={{ fontSize: "0.8rem", color: "#444" }}>
                                                                                From: {senderName} — {comm.payload?.[0]?.contentString?.slice(0, 100) || "(No content)"}
                                                                            </div>
                                                                            <small>Sent: {formatDate(comm.sent)}</small>
                                                                        </div>

                                                                        {/* ⭐ ⏰ Status controls */}
                                                                        <span className="d-flex align-items-center gap-2">
                                                                            <FaStar
                                                                                title={isStarred ? "Unstar this message" : "Star this message"}
                                                                                className={`${styles.starIcon} ${isStarred ? styles.starIconActive : ""}`}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    console.log("clicked");
                                                                                    if (isStarred) {
                                                                                        communicationService.removeStatusFlag(comm.id, "starred", contextProvider.id, dispatch);
                                                                                        toggleLocalFlag(comm.id, "starred", false);
                                                                                    } else {
                                                                                        communicationService.addStatusFlag(comm.id, "starred", contextProvider.id, dispatch);
                                                                                        toggleLocalFlag(comm.id, "starred", true);
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <FiClock
                                                                                title={isSnoozed ? "Unsnooze this message" : "Snooze this message"}
                                                                                style={{
                                                                                    cursor: "pointer",
                                                                                    color: isSnoozed ? "#f00de8ff" : "#6c757d",
                                                                                    fontSize: "1.2rem"
                                                                                }}
                                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    if (isSnoozed) {
                                                                                        communicationService.removeStatusFlag(comm.id, "snoozed", contextProvider.id, dispatch);
                                                                                        toggleLocalFlag(comm.id, "snoozed", false);
                                                                                    } else {
                                                                                        communicationService.addStatusFlag(comm.id, "snoozed", contextProvider.id, dispatch);
                                                                                        toggleLocalFlag(comm.id, "snoozed", true);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </span>
                                                                    </div>
                                                                </ListGroup.Item>
                                                            );
                                                        })}
                                                    </ListGroup>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        ) : null
                                        )}
                                    </Accordion>
                                )}

                                {fetchError && <Alert variant="danger">{fetchError}</Alert>}
                                {/* Reply Buttons */}
                                <Row className="mt-2">
                                    <Col md={12} className="text-start">
                                        {/* DISABLED FOR NOW......<Modal show={showThanksModal} onHide={() => setShowThanksModal(false)} centered> disabling for now*/}
                                        <QuickThanksModal show={showThanksModal} onClose={() => setShowThanksModal(false)} />
                                    </Col>
                                </Row>
                            </>
                        ) : (
                            <div>
                                {loading ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "150px" }}>
                                        <Spinner animation="border" role="status" variant="primary">
                                            <span className="visually-hidden">Loading inbox...</span>
                                        </Spinner>
                                        <p className="mt-2" style={{ color: "gray" }}>Loading inbox…</p>
                                    </div>
                                ) : commArray.length === 0 ? (
                                    <p style={{ color: "black" }}>Communications</p>
                                ) : (
                                    <div></div>
                                )}
                            </div>
                        )

                        }
                        {/* End Viewer */}
                    </div>

                </Col>
                <Col md={7}>
                    <InfoBar
                        fhirClient={fhirClient}
                        contextPatient={fetchedPatient}
                        providerData={fetchedProvider}
                        encounterData={fetchedEncounter}
                        layout="horizontal" // Optional, can be "vertical" or "horizontal"
                    />


                    <ThreadedMessageViewer
                        selectedComm={selectedComm}
                        fhirClient={fhirClient}
                        contextProvider={contextProvider}
                        communicationService={communicationService}
                        userSelection={true}
                        fetchedPatient={fetchedPatient}
                        fetchedProvider={fetchedProvider}
                        fetchedEncounter={fetchedEncounter}
                        setShowThanksModal={setShowThanksModal}
                        setShowReplyModal={setShowReplyModal}
                        setSelectedComm={setSelectedComm}
                        onSendLocalReply={(msg) => {
                            setSelectedComm((prev) => ({
                                ...prev,
                                injectedMessages: [...(prev?.injectedMessages || []), msg]
                            }));
                        }}
                    />

                </Col>
            </Row>

            <ResourceViewerModal
                show={showCommModal}
                onClose={handleCommModalClose}
                resource={modalCommDetails}
                //title="Communication Details"
                fhirClient={fhirClient}
                onReply={(comm) => {
                    setShowReplyModal(true);
                }}
                onReplyAll={(comm) => {
                    setShowReplyModal(true);
                }}
                contextProvider={contextProvider}
            />


            <CommunicationFormModal
                show={showReplyModal}
                onClose={() => setShowReplyModal(false)}
                formSubmitted={formSubmitted}
                submissionResult={submissionResult}
                setFormSubmitted={setFormSubmitted}
                setSubmissionResult={setSubmissionResult}
                selectMessageById={selectMessageById}
                setSelectedComm={setSelectedComm}
                contextProvider={contextProvider}
                contextEncounter={contextEncounter}
                replyToCommId={replyToCommId}
                fetchedPatient={isReplyMode ? fetchedPatient : contextPatient}
                fetchedEncounter={isReplyMode ? fetchedEncounter : contextEncounter}
                encounter={fetchedEncounter}
                communicationService={communicationService}
                resolvedRecipients={resolvedRecipients}
                fhirClient={fhirClient}
                selectedComm={selectedComm}
                isReplyMode={isReplyMode}
                scrollable 

            />

            {/* FHIR Resource Viewer Modal */}
            {showViewer && (
                <ResourceViewerModal
                    show={showViewer}
                    resource={viewResource}
                    onClose={() => setShowViewer(false)}
                />
            )}
        </Container>

    );
}

export default Inbox;